import {
  BlackoutDay,
  DayType,
  ExpirationPolicy,
  LoyaltyProgram,
  RewardTiers,
  Terminology,
  Group,
  Restaurant,
  RestaurantAddress,
  RestaurantAvailableStatus,
  Time,
  WorkDay,
  WorkTime
} from '../models/restaurant.model';
import {getTimezone} from 'countries-and-timezones';
import {dataToMinMaxRestrictions} from './min-max-order.dto';
import {EstimateOrder} from '../models/order.model';

export const dataToRestaurants = (data: any[]): Restaurant[] => data.map(restaurant => dataToRestaurant(restaurant));
export const dataToRestaurant = (venue: any): Restaurant => {
  const restaurant = venue.kyoo_location;
  const availableStatus = dataToRestaurantAvailableStatus(venue);
  return new Restaurant(
    restaurant.location_id,
    restaurant.merchant_id,
    restaurant.display_name,
    restaurant.logo_image_url,
    restaurant.image_url,
    restaurant.banner_image_url || './assets/images/back.jpg',
    restaurant.location_header_title,
    restaurant.location_header_text,
    restaurant.brand_color,
    restaurant.secondary_brand_color,
    restaurant.background_color,
    restaurant.secondary_background_color,
    restaurant.border_color,
    restaurant.primary_text_color,
    restaurant.secondary_text_color,
    restaurant.warning_text_color,
    availableStatus.customCSS,
    restaurant.mobile_enable,
    restaurant.tipping_enabled,
    restaurant.tabs_enabled,
    restaurant.loyalty_enabled,
    restaurant.loyalty_points_name,
    availableStatus.isSharedCardsEnabled,
    restaurant.allow_scheduled_pickups,
    restaurant.auth_amount_in_cents,
    restaurant.show_hours,
    restaurant.disable_asap,
    restaurant.select_asap_by_default,
    restaurant.disable_quick_checkout,
    restaurant.show_taxes_and_fees_breakout,
    restaurant.enforce_inventory,
    restaurant.hide_orders_ahead,
    venue.show_age_gate,
    restaurant.show_all_locations_button,
    restaurant.print_ahead_duration,
    restaurant.pickup_window_duration,
    restaurant.pickup_days_in_advance,
    restaurant.collapse_categories,
    restaurant.require_billing_address,
    restaurant.require_email,
    restaurant.enable_apple_google_pay,
    restaurant.enable_gift_cards,
    restaurant.enable_strict_tab_language,
    restaurant.enable_promo_codes,
    restaurant.handoff_location_numbered,
    restaurant.hide_all_signs,
    restaurant.disable_today_orders,
    restaurant.show_all_time_restricted_items,
    restaurant.show_all_fulfillment_restricted_items,
    restaurant.show_promo_code_field,
    restaurant.square_location_data.status,
    restaurant.minimum_curbside_amount_in_cents,
    restaurant.minimum_handoff_amount_in_cents,
    restaurant.minimum_pickup_amount_in_cents,
    restaurant.minimum_tableside_amount_in_cents,
    true, //display large product images only true now
    restaurant.cancellation_deadline_duration,
    availableStatus,
    availableStatus.blackoutDateRanges.map((day: any) => new BlackoutDay(
        day.id,
        day.location_id,
        day.enable,
        getBlackoutName(day.name),
        getRestaurantWorkDate(new Date(day.start_datetime), restaurant.square_location_data.timezone),
        getRestaurantWorkDate(new Date(day.end_datetime), restaurant.square_location_data.timezone)
      )
    ),
    availableStatus.country,
    new RestaurantAddress(
      restaurant.kyoo_address.country,
      restaurant.kyoo_address.address_line1,
      restaurant.kyoo_address.locality,
      restaurant.kyoo_address.administrative_district_level1,
      restaurant.kyoo_address.postal_code,
      restaurant?.square_location_data?.coordinates
    ),
    availableStatus.currency,
    availableStatus.hours.map((hour: any) => new WorkDay(
        hour.id,
        hour.location_id,
        hour.enable,
        new DayType(hour.day_type),
        getRestaurantTimeRanges(hour.time_ranges, true),
        getRestaurantTimeRanges(hour.time_ranges, false)
      )
    ),
    availableStatus.tipAmounts,
    availableStatus.tipType,
    restaurant.square_location_data.timezone,
    availableStatus.isAcceptingOrders,
    availableStatus.nextOpenTimeUtc,
    availableStatus.estimatedTimeToBeginPreparation,
    availableStatus.hideETA,
    availableStatus.numPersonsAhead,
    availableStatus.maxOrdersInQueue,
    availableStatus.placeInLine,
    restaurant.estimated_prep_time_duration,
    restaurant.addl_delivery_prep_time_duration,
    restaurant.person_name,
    restaurant.position,
    restaurant.description,
    restaurant.contact_phone,
    restaurant.default_kyoo_fee,
    restaurant.default_auto_gratuity_percent,
    restaurant.close_inactive_tabs_after_minutes,
    availableStatus.loyaltyPointsName,
    restaurant.auto_select_fulfillment,
    restaurant.show_window_end_time,
    restaurant.service_charge_name,
    restaurant.gift_cards_permissions_requested ? restaurant.gift_cards_permissions_requested : false,
    restaurant.select_soonest_available_by_default,
    restaurant.kyoo_location_data.order_confirmation_page_text,
    null,
    restaurant.kyoo_location_data.enable_restaurant_tip,
    restaurant.kyoo_location_data.default_delivery_auto_gratuity_percent,
    dataToMinMaxRestrictions(restaurant.kyoo_location_data),
    availableStatus.enableSoldOutOrders,
    restaurant.order_throttling_enabled,
    restaurant.show_addons,
    restaurant.kyoo_location_data.show_seller_delivery_locations,
    restaurant.kyoo_location_data.seller_delivery_locations,
    new RestaurantAddress(
      restaurant.kyoo_location_data.seller_dispatch_address?.country,
      restaurant.kyoo_location_data.seller_dispatch_address?.address_line1,
      restaurant.kyoo_location_data.seller_dispatch_address?.locality,
      restaurant.kyoo_location_data.seller_dispatch_address?.administrative_district_level1,
      restaurant.kyoo_location_data.seller_dispatch_address?.postal_code
    ),
    restaurant?.courier_powered_delivery_enabled,
    restaurant?.seller_powered_delivery_enabled,
    restaurant?.kyoo_location_data?.tableside_texts?.table_number_required,
    restaurant?.enable_shared_orders,
    restaurant?.enable_shared_order_single_order_received_sms,
    restaurant?.display_gift_card_balance,
    venue?.allow_edit_table_number,
    restaurant?.display_kyoo_service_fee_on_menu,
    restaurant?.require_phone_verification,
    restaurant?.collapse_modifier_and_variation,
    restaurant?.hide_pickup_time,
    venue.enable_3d_secure,
    venue.enable_parent_sub_categories,
    restaurant?.enforce_accept_order_throttling,
    restaurant?.enable_favorite_items,
    venue.enable_ikon_pass,
    restaurant?.hide_sold_out_items,
    restaurant?.show_sold_out_items_last,
    restaurant?.hide_items_outside_daily_availability,
    restaurant?.show_not_currently_sold_category,
    venue?.qr_code_ordering_only,
    venue?.qr_code_ordering_only_error_message || 'Please scan the table specific QR codes to place your order to ensure timely delivery',
    venue?.atrium_enabled,
    venue?.atrium_connect_url,
    restaurant?.show_hours,
    restaurant?.disable_for_pickup_at,
    restaurant?.show_dietary_icons,
    restaurant?.show_dietary_filter,
    restaurant?.auto_collapse_categories || 0,
    restaurant?.display_item_images_on_menu,
    restaurant?.apply_prep_time_before_opening,
    restaurant?.hide_availability_messages,
    restaurant?.show_quick_add_button !== false,
    restaurant?.table_number_type,
    restaurant?.schedule_time_before_menu,
    venue?.contact_channel,
    restaurant?.contact_phones,
    restaurant?.contact_emails,
    restaurant?.display_contact_phones,
    restaurant?.display_contact_emails,
    restaurant?.use_flavor_shifts_time,
    venue?.absorb_kyoo_fee,
    venue?.service_provider
  );
};

export const dataToGroup = (data: any): Group => {
  return new Group(
    data.group_id,
    data.display_name,
    data.brand_color,
    data.background_color,
    data.border_color,
    data.primary_text_color,
    data.secondary_text_color,
    data.banner_image_url || './assets/images/back.jpg',
    data.logo_image_url,
    data.phone_number,
    data.tip_amounts.split(',').map(Number),
    data.tip_type,
    data.tipping_enabled,
    data.group_ids,
    data.group_header_text,
    data.group_header_title,
    new RestaurantAddress(
      data.kyoo_address.country,
      data.kyoo_address.address_line1,
      data.kyoo_address.locality,
      data.kyoo_address.administrative_district_level1,
      data.kyoo_address.postal_code,
      data?.square_location_data?.coordinates
    ),
    data.additional_location_kyoo_fee,
    data.additional_location_delivery_kyoo_fee,
    data.additional_location_tab_kyoo_fee,
    data.base_kyoo_fee,
    data.base_delivery_kyoo_fee,
    data.base_tab_kyoo_fee,
    data.country,
    data.default_auto_gratuity_percent,
    data.seller_powered_delivery_enabled,
    data.courier_powered_delivery_enabled,
    data.combine_group_locations,
    data.use_flavor_shifts,
    data.vanity_link,
    data.close_inactive_tabs_after_minutes,
  );
};

export const dataToRestaurantAvailableStatus = (venue: any): RestaurantAvailableStatus => {
  return {
    isAcceptingOrders: venue.isAcceptingOrders,
    isCurbsideEnabled: venue.isCurbsideEnabled,
    isTablesideEnabled: venue.isTablesideEnabled,
    isDeliveryEnabled: venue.kyoo_location.seller_powered_delivery_enabled || venue.kyoo_location.courier_powered_delivery_enabled,
    isPickupEnabled: venue.isPickupEnabled,
    isXSideEnabled: venue.isXSideEnabled,
    isFridgeEnabled: venue.isFridgeEnabled,
    handoffLocation: venue.kyooLocationData ? venue.kyooLocationData.handoff_location : null,
    deliverySeller: venue.kyoo_location.seller_powered_delivery_enabled,
    deliveryCourier: venue.kyoo_location.courier_powered_delivery_enabled,
    deliverySellerMinAmount: venue.kyooLocationData ? venue.kyooLocationData.seller_min_order_amount : 0,
    deliveryCourierMinAmount: venue.kyooLocationData ? venue.kyooLocationData.courier_min_order_amount : 0,
    isHidden: venue.isHidden,
    nextOpenTimeUtc: venue.nextOpenTimeUtc,
    estimatedTimeToBeginPreparation: venue.estimatedTimeToBeginPreparation,
    hideETA: venue.hideETA,
    numPersonsAhead: venue.numPersonsAhead,
    maxOrdersInQueue: venue.maxOrdersInQueue,
    placeInLine: venue.placeInLine,
    hours: venue.hours,
    blackoutDateRanges: venue.blackout_date_ranges,
    tipAmounts: venue.tip_amounts,
    tipType: venue.tip_type,
    country: venue.country,
    currency: venue.currency,
    isSharedCardsEnabled: venue.shared_cards_enabled,
    customCSS: venue.custom_css,
    loyaltyPointsName: venue.loyalty_program ? {
      one: venue.loyalty_program.terminology.one.toLowerCase(),
      other: venue.loyalty_program.terminology.other.toLowerCase()
    } : null,
    gift_cards_permissions_requested: isValue(venue.gift_cards_permissions_requested),
    default_delivery_auto_gratuity_percent: venue.kyoo_location.kyoo_location_data.default_delivery_auto_gratuity_percent,
    enable_restaurant_tip: venue.kyoo_location.kyoo_location_data.enable_restaurant_tip,
    enableSoldOutOrders: venue.kyoo_location.enable_order_ahead_sold_out_items
  };
};

export function getBlackoutName(name): string {
  if (name !== null && name.length > 0 ) {
    return name;
  }
  return 'Special hours';
}

export function getRestaurantWorkDate(dayDate: Date, timezone: string): Date {
  const timezoneData = getTimezone(timezone);
  return new Date(dayDate.getTime() - (Math.abs(timezoneData.utcOffset) - dayDate.getTimezoneOffset()) * 60000);
}

export function getRestaurantTimeRanges(timeRanges: any, isStartTime: boolean): WorkTime[] {
  const times: WorkTime[] = [];
  for (const time of timeRanges) {
    if (isStartTime) {
      times.push(new WorkTime(new Time(time.start_time.time.hour, time.start_time.time.minute), time.start_time.meridian));
    } else {
      times.push(new WorkTime(new Time(time.end_time.time.hour, time.end_time.time.minute), time.end_time.meridian));
    }
  }
  return times;
}

export function isValue(data: any): boolean {
  if (data) {
    return data;
  }
  return false;
}

export const dataToEstimateOrders = (data: any[]): EstimateOrder[] => data.map(estimateOrder => dataToEstimateOrder(estimateOrder));

export const dataToEstimateOrder = (data: any): EstimateOrder => {
  return new EstimateOrder(
    data.catalog_object_id,
    data.category_id,
    data.quantity,
    parseDuration(data?.duration)
  );
};

export const parseDuration = (duration): string => {
  if (!duration) {
    return null;
  }
  const regex = /PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/;
  const matches = duration.match(regex);

  if (!matches) {
    return null;
  }

  const hours = matches[1] ? parseInt(matches[1], 10) : 0;
  const minutes = matches[2] ? parseInt(matches[2], 10) : 0;
  const seconds = matches[3] ? parseInt(matches[3], 10) : 0;

  if (hours === 0 && minutes === 0 && seconds === 0) {
    return null;
  }

  let result = '';
  if (hours > 0) {
    result += hours + ' ' + (hours === 1 ? 'hour' : 'hours') + ' ';
  }
  if (minutes > 0) {
    result += minutes + ' ' + (minutes === 1 ? 'min' : 'mins') + ' ';
  }
  if (seconds > 0) {
    result += seconds + ' ' + (seconds === 1 ? 'sec' : 'secs');
  }

  return result.trim();
};

export const modifierSoldOut = (locationOverrides: any[], locationId): boolean => {
  if (locationOverrides && locationOverrides.length) {
    const locationOverride = locationOverrides.find(location => locationId === location.location_id);
    if (locationOverride) {
      return locationOverride.sold_out;
    }
  }
  return false;
};

export function faker(length): string {
  let result = '';
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;
  let counter = 0;
  while (counter < length) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
    counter += 1;
  }
  return result;
}

export const dataToLoyaltyProgram = (data:any):LoyaltyProgram => {
  const rewardTiers = data.reward_tiers.map((reward_tier:any) => new RewardTiers(reward_tier.id, reward_tier.name, reward_tier.points, reward_tier.created_at, reward_tier.definition));
  const expirationPolicy = new ExpirationPolicy(data.expiration_policy);
  const terminology = new Terminology(data.terminology.one,data.terminology.other);
  const locationIds = data.location_ids;
  const accrualRules = data.accrual_rules
  return new LoyaltyProgram(
    data.status,
    rewardTiers,
    expirationPolicy,
    terminology,
    locationIds,
    accrualRules
  );
};
export const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const getRestaurantHours = (restaurant: Restaurant): any[] => {
  const today = new Date();
  const result = [];

  const shortDaysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  for (let i = 0; i < 7; i++) {
    const currentDate = new Date();
    currentDate.setDate(today.getDate() + i);

    const dayOfWeek = currentDate.getDay();
    const shortDayName = shortDaysOfWeek[dayOfWeek];

    const daySchedule = restaurant.hoursOfOperation.find(day => day.dayType.dayType.toUpperCase() === daysOfWeek[dayOfWeek].toUpperCase());

    const isBlackout = restaurant.blackoutDateRanges.some(range => {
      return range.enable &&
        currentDate >= new Date(range.startDate) &&
        currentDate <= new Date(range.endDate);
    });

    const formattedDate = `${shortDayName} (${currentDate.getMonth() + 1}/${currentDate.getDate()}):`;
    if (daySchedule && !isBlackout) {
      const schedule = getDaySchedule(daySchedule);
      result.push(`${formattedDate} ${schedule}`);
    } else {
      result.push(`${formattedDate} Closed`);
    }
  }

  return result;
};
export const getDaySchedule = (daySchedule: WorkDay): string => {
  if (!daySchedule.enable) {
    return 'Closed';
  }

  return daySchedule.startTime.map((start, index) => {
    const end = daySchedule.endTime[index];
    return `${formatTime(start)} - ${formatTime(end)}`;
  }).join(', ');
};

export const formatTime = (time: WorkTime): string => {
  const hour = time.time.hour % 12 || 12;
  const minute = time.time.minute.toString().padStart(2, '0');
  const period = time.meridian ? 'AM' : 'PM';
  return `${hour}:${minute} ${period}`;
};

